import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { IonContent, ModalController } from '@ionic/angular';
import { ChatService, Message, Thread } from '../chats.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { ImagePickerComponent } from 'src/app/shared/pickers/image-picker/image-picker.component';
import { ImageCropperComponent } from 'src/app/shared/image-cropper/image-cropper.component';
import { PhotoService } from 'src/app/shared/services/photo.service';
import { VoiceService } from '../../shared/services/voice.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.page.html',
  styleUrls: ['./chat.page.scss'],
})
export class ChatPage implements OnInit {
  @ViewChild(IonContent) content!: IonContent;
  @ViewChild(ImagePickerComponent) imagePicker!: ImagePickerComponent;
  @ViewChild('audio', { static: true }) audioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('seek', { static: true }) seekRef!: ElementRef<HTMLInputElement>;
  tid!: string;
  me!: string;
  otherUid!: string;
  otherName = '';
  messages: Message[] = [];
  newMessage = '';
  subMsgs?: Subscription;
  subThread?: Subscription;
  subUser?: Subscription;
  private inView = false;
  private markTimer: any;
  recState: 'idle' | 'rec' | 'stopping' = 'idle';
  private playing: Record<string, HTMLAudioElement> = {};
  currentTime: Record<string, number> = {};
  olderBuffer: Message[] = [];
  isLoadingOlder = false;
  hasMoreOlder = false;
  nearBottom = true;
  firebaseUrl = 'https://firebasestorage.googleapis.com/v0/b/mimboapp-d1224.firebasestorage.app/o/'
  constructor(private route: ActivatedRoute,
    private afAuth: AngularFireAuth,
    private chatService: ChatService,
    private afs: AngularFirestore,
    private modalCtrl: ModalController,
    private photoService: PhotoService,
    private voiceService: VoiceService,) { }

  async ngOnInit() {
    this.tid = this.route.snapshot.paramMap.get('tid')!;
    const u = await this.getUser();
    if (!u) {
      return;
    }
    if (!u) return;
    this.me = u.uid;
    await this.chatService.markThreadRead(this.tid);
    this.subThread = this.afs.doc<Thread>(`threads/${this.tid}`).valueChanges({ idField: 'id' })
      .subscribe(t => {
        if (!t) return;
        this.otherUid = t.participants[0] === this.me ? t.participants[1] : t.participants[0];
        if (!this.subUser) {
          this.subUser = this.chatService.observeUser(this.otherUid).subscribe(async u2 => {
            this.otherName = (u2?.displayName || await this.chatService.getUsernameById(this.otherUid) || 'User').trim();
          });
        }
      });
    this.subMsgs = this.chatService.observeMessages(this.tid).subscribe(async ms => {
      // merge older buffer + live tail, dedupe by id (ascending preserved)
      const mapById = new Map<string, Message>();
      [...this.olderBuffer, ...ms].forEach(m => { if (m.id) mapById.set(m.id, m); });
      this.messages = Array.from(mapById.values());

      this.markThreadReadDebounced();

      if (this.nearBottom) {
        setTimeout(() => this.content?.scrollToBottom(200), 300);
      }
      this.hasMoreOlder = await this.chatService.hasOlder(this.tid);
    });
  }
  async ionViewDidEnter() {
    this.inView = true;
  }

  ionViewWillLeave() {
    this.inView = false;
    clearTimeout(this.markTimer);
    // one last sweep in case a message landed right before leaving
    this.chatService.markThreadRead(this.tid).catch(() => { });
  }
  onKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }
  ngOnDestroy() {
    this.subMsgs?.unsubscribe();
    this.subThread?.unsubscribe();
    this.subUser?.unsubscribe();
  }
  async send() {
    const t = (this.newMessage || '').trim();
    if (!t) return;
    debugger;
    await this.chatService.sendMessage(this.tid, t);
    this.newMessage = '';
    setTimeout(() => this.content?.scrollToBottom(0), 0);
  }

  isMine(m: Message) { return m.senderId === this.me; }

  time(ms: number) {
    const d = new Date(ms);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  async getUser() {
    let user = await this.afAuth.currentUser;
    if (!user) {
      user = await new Promise(resolve => {
        const sub = this.afAuth.authState.subscribe(u => {
          resolve(u ?? null);
          sub.unsubscribe();
        });
      });
    }
    return user ?? null;
  }

  private markThreadReadDebounced() {
    if (!this.inView) return;
    clearTimeout(this.markTimer);
    this.markTimer = setTimeout(() => this.chatService.markThreadRead(this.tid), 250);
  }

  pickImage() {
    this.imagePicker.openActionSheet();
  }
  onPhotoCaptured(event: { imageUri: string }) {
    this.openImageCropper(event.imageUri);
  }

  openImageCropper(imageuri: string) {
    this.modalCtrl
      .create({
        component: ImageCropperComponent,
        componentProps: {
          uploadedImage: imageuri,
        },
      })
      .then((modalEl) => {
        modalEl.onDidDismiss().then((modalData) => {
          if (!modalData.data) {
            return;
          }
          var imageUrl = modalData.data['imageUrl'];
          var blob = this.photoService.base64ToBlob(imageUrl, 'image/jpeg');
          this.chatService.sendImageMessage(this.tid, blob, 'jpg')
        });
        modalEl.present();
      });
  }
  async startRec() {
    try {
      await this.voiceService.start(120000, true); // auto-stop at 120s, save to file
      this.recState = 'rec';
    } catch (e: any) {
      alert(e.message || 'Recording not available');
    }
  }

  async stopRec() {
    if (this.recState !== 'rec') return;
    this.recState = 'stopping';
    try {
      const { blob, durationMs } = await this.voiceService.stop();
      await this.chatService.sendVoiceMessage(this.tid, blob, durationMs);
    } finally {
      this.recState = 'idle';
    }
  }

  async deleteRec() {
    if (this.recState !== 'rec') return;
    try {
      await this.voiceService.delete();
    } finally {
      this.recState = 'idle';
    }
  }

  getMediaUrl(message: Message) {
    return this.firebaseUrl + message.media.path.replace(/\//g, '%2F') + '?alt=media'
  }
  openImage(m: Message) {
    this.photoService.open(this.getMediaUrl(m))
  }
  isPlaying(m: Message) { return !!this.playing[m.id]; }
  togglePlay(m: Message) {
    const audio = (document.querySelector(`#audio-${m.id}`) as HTMLAudioElement)
      || (this.findAudioForMessage(m));
    if (!audio) return;

    if (this.playing[m.id]) {
      audio.pause();
      delete this.playing[m.id];
    } else {
      // pause any other playing
      Object.values(this.playing).forEach(a => a.pause());
      this.playing = {};
      audio.play().catch(console.error);
      this.playing[m.id] = audio;
    }
  }

  onEnded(m: Message) {
    delete this.playing[m.id];
    this.currentTime[m.id] = 0;
  }

  onTime(m: Message, t: number) {
    this.currentTime[m.id] = t;
  }

  renderTime(sec: number) {
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    const m = Math.floor(sec / 60).toString();
    return `${m}:${s}`;
  }

  private findAudioForMessage(m: Message) {
    // If you use ViewChildren, wire it properly; quick fallback:
    return (document.querySelector(`audio[src="${this.getMediaUrl(m)}"]`) as HTMLAudioElement) ?? null;
  }

  getMimeTypeFromExtension(m: Message) {
    const extension = m.media.path.split('.').pop().toLowerCase();
    const mimeTypes = {
      'm4a': 'audio/mp4',
      'aac': 'audio/aac',
      'mp3': 'audio/mpeg',
      'wav': 'audio/wav',
      'ogg': 'audio/ogg',
      'webm': 'audio/webm',
      '3gp': 'audio/3gpp',
      'flac': 'audio/flac',
    };

    return mimeTypes[extension] || 'application/octet-stream';
  }
  getProgress(m: Message, player: HTMLAudioElement): number {
    const duration = (m.media.durationMs || 0) / 1000;
    const current = this.currentTime[m.id] || 0;
    return duration > 0 ? (current / duration) * 100 : 0;
  }

  seek(m: Message, player: HTMLAudioElement, event: MouseEvent) {
    const progressBar = event.currentTarget as HTMLElement;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;

    const duration = (m.media.durationMs || 0) / 1000;
    player.currentTime = duration * percentage;
  }

  onLoadedMetadata(m: Message, player: HTMLAudioElement) {
    // Optional: Update duration if not stored in message
    if (!m.media.durationMs) {
      m.media.durationMs = player.duration * 1000;
    }
  }

  threadImageUploadCount() {
    return this.chatService.activeImageUploads[this.tid] || 0;
  };
  threadVoiceUploadCount() {
    return this.chatService.activeVoiceUploads[this.tid] || 0;
  };
  async loadOlder() {
    if (this.isLoadingOlder) return;
    this.isLoadingOlder = true;

    const el = await this.content.getScrollElement();
    const prevH = el.scrollHeight;

    try {
      const chunk = await this.chatService.loadOlderOnce(this.tid, 50);
      if (chunk.length) {
        // prepend + dedupe
        const mapById = new Map<string, Message>();
        [...chunk, ...this.olderBuffer].forEach(m => { if (m.id) mapById.set(m.id, m); });
        this.olderBuffer = Array.from(mapById.values());

        // rebuild full list (olderBuffer + live tail without duplicates)
        const liveTail = this.messages.filter(m => !this.olderBuffer.find(o => o.id === m.id));
        this.messages = [...this.olderBuffer, ...liveTail];

        // keep viewport stable
        setTimeout(async () => {
          const after = await this.content.getScrollElement();
          await this.content.scrollByPoint(0, after.scrollHeight - prevH, 0);
        }, 0);
      }

      // update flag (short-circuit if we got less than a full page)
      this.hasMoreOlder = chunk.length === 50 ? await this.chatService.hasOlder(this.tid) : false;
    } finally {
      this.isLoadingOlder = false;
    }
  }
  async onScroll() {
    const el = await this.getScrollEl();
    if (!el) return;

    // near-bottom guard
    const thresholdPx = 120;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    this.nearBottom = distFromBottom < thresholdPx;

    // auto-prefetch near top (optional)
    if (el.scrollTop < 200 && this.hasMoreOlder && !this.isLoadingOlder) {
      await this.loadOlder();
    }
  }
  trackById = (_: number, m: Message) => m.id;
  // chat.page.ts
  private async getScrollEl(): Promise<HTMLElement | null> {
    try {
      if (!this.content) return null;
      const el = await this.content.getScrollElement();
      return el ?? null;
    } catch {
      return null;
    }
  }

}
